import React, { useState } from 'react';
import styled from '@emotion/styled';
import { useGetLeadsQuery, useGetLeadStatsQuery } from '../../api/leadsApi';
import type { Lead, LeadsFilters } from '../../api/leadsApi';
import { useAppSelector } from '../../hooks/redux';
import OnboardingTrigger from '../onboarding/OnboardingTrigger';

const Container = styled.div`
  padding: 24px;
  max-width: 1200px;
  margin: 0 auto;
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 24px;
  gap: 16px;
  flex-wrap: wrap;
`;

const HeaderLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  flex: 1;
`;

const Title = styled.h1`
  font-size: 24px;
  font-weight: 600;
  color: #1a202c;
  margin: 0;
`;

const SearchContainer = styled.div`
  display: flex;
  gap: 12px;
  align-items: center;
  flex-wrap: wrap;
`;

const SearchInput = styled.input`
  padding: 8px 12px;
  border: 1px solid #e2e8f0;
  border-radius: 6px;
  min-width: 200px;
  font-size: 14px;
  
  &:focus {
    outline: none;
    border-color: #4299e1;
    box-shadow: 0 0 0 3px rgba(66, 153, 225, 0.1);
  }
`;

const Select = styled.select`
  padding: 8px 12px;
  border: 1px solid #e2e8f0;
  border-radius: 6px;
  font-size: 14px;
  background: white;
  
  &:focus {
    outline: none;
    border-color: #4299e1;
    box-shadow: 0 0 0 3px rgba(66, 153, 225, 0.1);
  }
`;

const StatsContainer = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 16px;
  margin-bottom: 24px;
`;

const StatCard = styled.div`
  background: white;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  padding: 16px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
`;

const StatLabel = styled.div`
  font-size: 12px;
  color: #718096;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin-bottom: 4px;
`;

const StatValue = styled.div`
  font-size: 24px;
  font-weight: 600;
  color: #2d3748;
`;

const TableContainer = styled.div`
  background: white;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  overflow: hidden;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
`;

const TableHead = styled.thead`
  background: #f7fafc;
`;

const TableHeader = styled.th`
  padding: 12px 16px;
  text-align: left;
  font-weight: 600;
  color: #4a5568;
  font-size: 12px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  border-bottom: 1px solid #e2e8f0;
`;

const TableRow = styled.tr<{ clickable?: boolean }>`
  &:hover {
    background: #f7fafc;
    ${({ clickable }) => clickable && `cursor: pointer;`}
  }
  
  &:not(:last-child) {
    border-bottom: 1px solid #e2e8f0;
  }
  
  ${({ clickable }) => clickable && `
    transition: background-color 0.15s;
  `}
`;

const TableCell = styled.td`
  padding: 12px 16px;
  color: #2d3748;
  font-size: 14px;
`;

const StatusBadge = styled.span<{ status: string }>`
  padding: 4px 8px;
  border-radius: 12px;
  font-size: 12px;
  font-weight: 500;
  text-transform: capitalize;
  
  ${({ status }) => {
    switch (status) {
      case 'new':
        return 'background: #bee3f8; color: #2a4365;';
      case 'contacted':
        return 'background: #fef5e7; color: #744210;';
      case 'qualified':
        return 'background: #c6f6d5; color: #22543d;';
      case 'closed':
        return 'background: #e2e8f0; color: #4a5568;';
      default:
        return 'background: #e2e8f0; color: #4a5568;';
    }
  }}
`;

const LoadingState = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 48px;
  color: #718096;
`;

const ErrorState = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 48px;
  color: #e53e3e;
  text-align: center;
`;

const PaginationContainer = styled.div`
  display: flex;
  justify-content: between;
  align-items: center;
  padding: 16px;
  background: #f7fafc;
  border-top: 1px solid #e2e8f0;
  gap: 16px;
  flex-wrap: wrap;
`;

const PaginationInfo = styled.div`
  color: #718096;
  font-size: 14px;
  flex: 1;
`;

const PaginationControls = styled.div`
  display: flex;
  gap: 8px;
  align-items: center;
`;

const PaginationButton = styled.button<{ disabled?: boolean }>`
  padding: 8px 12px;
  border: 1px solid #e2e8f0;
  border-radius: 6px;
  background: white;
  color: #4a5568;
  font-size: 14px;
  cursor: pointer;
  
  &:hover:not(:disabled) {
    background: #f7fafc;
  }
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const EmptyState = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  padding: 48px;
  color: #718096;
  text-align: center;
`;

const MessagePreview = styled.div`
  max-width: 200px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-size: 12px;
  color: #718096;
  margin-top: 4px;
`;

interface LeadsListProps {
  onLeadSelect?: (lead: Lead) => void;
}

export const LeadsList: React.FC<LeadsListProps> = ({ onLeadSelect }) => {
  const [filters, setFilters] = useState<LeadsFilters>({
    page: 1,
    limit: 20,
  });

  const { user } = useAppSelector(state => state.auth);
  const { data: leadsData, error: leadsError, isLoading: leadsLoading } = useGetLeadsQuery(filters);
  const { data: statsData, error: statsError } = useGetLeadStatsQuery();

  // Helper function to check if error is onboarding related
  const isOnboardingError = (error: any) => {
    if (!error) return false;
    if ('data' in error && error.data && typeof error.data === 'object') {
      // Check both error.data.message and error.data.error for the onboarding message
      const errorMessage = error.data.message || error.data.error || '';
      return errorMessage.includes('Customer account setup required') ||
             errorMessage.includes('complete onboarding');
    }
    return false;
  };

  const handleSearchChange = (search: string) => {
    setFilters(prev => ({ ...prev, search: search || undefined, page: 1 }));
  };

  const handleStatusFilter = (status: string) => {
    setFilters(prev => ({
      ...prev,
      status: status === 'all' ? undefined : status as 'new' | 'contacted' | 'qualified' | 'closed',
      page: 1
    }));
  };

  const handleSourceFilter = (source: string) => {
    setFilters(prev => ({
      ...prev,
      source: source === 'all' ? undefined : source,
      page: 1
    }));
  };

  const handlePageChange = (page: number) => {
    setFilters(prev => ({ ...prev, page }));
  };

  const handleLeadClick = (lead: Lead) => {
    if (onLeadSelect) {
      onLeadSelect(lead);
    }
  };

  if (leadsLoading) {
    return (
      <Container>
        <LoadingState>Loading leads...</LoadingState>
      </Container>
    );
  }

  if (leadsError) {
    // Check if this is an onboarding error
    if (isOnboardingError(leadsError) || isOnboardingError(statsError)) {
      return (
        <Container>
          <Header>
            <HeaderLeft>
              <Title>Leads</Title>
            </HeaderLeft>
          </Header>
          <OnboardingTrigger
            icon="📧"
            title="Complete Your Account Setup"
            description="You need to complete your account setup before you can access leads. This will only take a few minutes."
            buttonText="Complete Setup"
          />
        </Container>
      );
    }

    return (
      <Container>
        <ErrorState>
          <div>Failed to load leads</div>
          <div>Please try again later</div>
        </ErrorState>
      </Container>
    );
  }

  const leads = leadsData?.data.leads || [];
  const pagination = leadsData?.data.pagination;
  const stats = statsData?.data;

  // Check if user needs onboarding
  const needsOnboarding = user?.role === 'customer' && (!user?.customerId || user?.customerId === null);
  const hasNoData = leads.length === 0 && !filters.search && !filters.status && !filters.source;


  // Show onboarding for customers without customerId when they have no leads and no active filters
  if (needsOnboarding && hasNoData) {
    return (
      <Container>
        <Header>
          <HeaderLeft>
            <Title>Leads</Title>
          </HeaderLeft>
        </Header>
        <OnboardingTrigger
          icon="📧"
          title="Start Capturing Leads!"
          description="Set up your account and create your first landing page to start collecting leads from potential customers."
          buttonText="Set Up My Account"
        />
      </Container>
    );
  }

  // Get unique sources for filter
  const uniqueSources = Array.from(new Set(leads.map(lead => lead.source)));

  return (
    <Container>
      <Header>
        <HeaderLeft>
          <Title>Leads</Title>
        </HeaderLeft>
        <SearchContainer>
          <SearchInput
            type="text"
            placeholder="Search leads..."
            onChange={(e) => handleSearchChange(e.target.value)}
            value={filters.search || ''}
          />
          <Select
            value={filters.status || 'all'}
            onChange={(e) => handleStatusFilter(e.target.value)}
          >
            <option value="all">All Status</option>
            <option value="new">New</option>
            <option value="contacted">Contacted</option>
            <option value="qualified">Qualified</option>
            <option value="closed">Closed</option>
          </Select>
          <Select
            value={filters.source || 'all'}
            onChange={(e) => handleSourceFilter(e.target.value)}
          >
            <option value="all">All Sources</option>
            {uniqueSources.map(source => (
              <option key={source} value={source}>{source}</option>
            ))}
          </Select>
        </SearchContainer>
      </Header>

      {stats && (
        <StatsContainer>
          <StatCard>
            <StatLabel>Total Leads</StatLabel>
            <StatValue>{stats.totalLeads}</StatValue>
          </StatCard>
          <StatCard>
            <StatLabel>New Leads</StatLabel>
            <StatValue>{stats.newLeads}</StatValue>
          </StatCard>
          <StatCard>
            <StatLabel>Contacted</StatLabel>
            <StatValue>{stats.contactedLeads}</StatValue>
          </StatCard>
          <StatCard>
            <StatLabel>Qualified</StatLabel>
            <StatValue>{stats.qualifiedLeads}</StatValue>
          </StatCard>
          <StatCard>
            <StatLabel>Closed</StatLabel>
            <StatValue>{stats.closedLeads}</StatValue>
          </StatCard>
        </StatsContainer>
      )}

      <TableContainer>
        <Table>
          <TableHead>
            <TableRow>
              <TableHeader>Lead</TableHeader>
              <TableHeader>Contact</TableHeader>
              <TableHeader>Source</TableHeader>
              <TableHeader>Status</TableHeader>
              <TableHeader>Customer</TableHeader>
              <TableHeader>Landing Page</TableHeader>
              <TableHeader>Created</TableHeader>
            </TableRow>
          </TableHead>
          <tbody>
            {leads.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7}>
                  <EmptyState>
                    <div>No leads found</div>
                    <div>Try adjusting your search criteria</div>
                  </EmptyState>
                </TableCell>
              </TableRow>
            ) : (
              leads.map((lead: Lead) => (
                <TableRow key={lead.id} clickable onClick={() => handleLeadClick(lead)}>
                  <TableCell>
                    <div>
                      <div style={{ fontWeight: 600 }}>{lead.name}</div>
                      <div style={{ fontSize: '12px', color: '#718096' }}>
                        {lead.email}
                      </div>
                      {lead.message && (
                        <MessagePreview title={lead.message}>
                          {lead.message}
                        </MessagePreview>
                      )}
                    </div>
                  </TableCell>
                  <TableCell>
                    <div style={{ fontSize: '12px' }}>
                      {lead.phone && <div>{lead.phone}</div>}
                    </div>
                  </TableCell>
                  <TableCell>
                    <code style={{
                      background: '#f7fafc',
                      padding: '2px 6px',
                      borderRadius: '4px',
                      fontSize: '12px'
                    }}>
                      {lead.source}
                    </code>
                  </TableCell>
                  <TableCell>
                    <StatusBadge status={lead.status}>{lead.status}</StatusBadge>
                  </TableCell>
                  <TableCell>
                    <div>
                      <div style={{ fontWeight: 500, fontSize: '12px' }}>
                        {lead.customer.name}
                      </div>
                      {lead.customer.company && (
                        <div style={{ fontSize: '11px', color: '#718096' }}>
                          {lead.customer.company}
                        </div>
                      )}
                    </div>
                  </TableCell>
                  <TableCell>
                    {lead.landingPage ? (
                      <div>
                        <div style={{ fontWeight: 500, fontSize: '12px' }}>
                          {lead.landingPage.title}
                        </div>
                        <div style={{ fontSize: '11px', color: '#718096' }}>
                          /{lead.landingPage.slug}
                        </div>
                      </div>
                    ) : (
                      <span style={{ color: '#718096', fontSize: '12px' }}>—</span>
                    )}
                  </TableCell>
                  <TableCell>
                    <div style={{ fontSize: '12px', color: '#718096' }}>
                      {new Date(lead.createdAt).toLocaleDateString()}
                    </div>
                    <div style={{ fontSize: '11px', color: '#a0aec0' }}>
                      {new Date(lead.createdAt).toLocaleTimeString([], { 
                        hour: '2-digit', 
                        minute: '2-digit' 
                      })}
                    </div>
                  </TableCell>
                </TableRow>
              ))
            )}
          </tbody>
        </Table>

        {pagination && (
          <PaginationContainer>
            <PaginationInfo>
              Showing {((pagination.page - 1) * pagination.limit) + 1} to{' '}
              {Math.min(pagination.page * pagination.limit, pagination.total)} of{' '}
              {pagination.total} leads
            </PaginationInfo>
            <PaginationControls>
              <PaginationButton
                disabled={pagination.page <= 1}
                onClick={() => handlePageChange(pagination.page - 1)}
              >
                Previous
              </PaginationButton>
              <span style={{ color: '#718096', fontSize: '14px' }}>
                Page {pagination.page} of {pagination.totalPages}
              </span>
              <PaginationButton
                disabled={pagination.page >= pagination.totalPages}
                onClick={() => handlePageChange(pagination.page + 1)}
              >
                Next
              </PaginationButton>
            </PaginationControls>
          </PaginationContainer>
        )}
      </TableContainer>
    </Container>
  );
};